import { Banner, Button, DataTable, EmptyState, Spinner, TextField, type Column } from '@ui';
import { Fragment, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { resolutionOf } from '../../../shared/board';
import { bugApi, reportLabel, type BugReportDetail, type BugReportListItem, type BugStatus, type RepeatMode } from '../bugApi';
import { SearchIcon } from '../icons';
import { formatDateTime } from '../labels';
import EpicFilter, { inEpic, type EpicIds } from './EpicFilter';
import RevealOnOpen from './RevealOnOpen';
import { RepeatBadge, RepeatFilter } from './RepeatBlock';
import { applyDecision, onRepeatDecided } from './repeatBus';
import { firstLine, reportValue, stageLabel } from './layout';

type OnError = (err: unknown, fallback: string) => void;
type Moved = (project: string, from: BugStatus, to: BugStatus | null) => void;
type RenderDetail = (id: number, onChanged: (report: BugReportDetail) => void, onDeleted: () => void) => ReactNode;

const PAGE = 30;
/** Pages of reports newest first (`nextBefore`, Дк3); a new search starts over after a short pause. */
function usePages(status: string | undefined, onError: OnError, repeats: RepeatMode = '') {
  const [text, setText] = useState('');
  const [reports, setReports] = useState<BugReportListItem[] | null>(null);
  const [next, setNext] = useState<number | null>(null);
  const first = useRef(true);

  const load = useCallback(
    async (before: number | null, q: string) => {
      try {
        const res = await bugApi.list({ status, q: q.trim() || undefined, before, limit: PAGE, repeats });
        setReports((prev) => (before ? [...(prev ?? []), ...res.reports] : res.reports));
        setNext(res.nextBefore);
      } catch (err) {
        onError(err, 'Не удалось загрузить репорты');
      }
    },
    [status, onError, repeats],
  );

  useEffect(() => {
    const delay = first.current ? 0 : 250;
    first.current = false;
    const timer = setTimeout(() => {
      setReports(null);
      void load(null, text);
    }, delay);
    return () => clearTimeout(timer);
  }, [load, text]);

  // Пов13: a decision on the open card changes the badge of the list at once.
  useEffect(() => onRepeatDecided((id, view) => setReports((prev) => (prev ? applyDecision(prev, id, view) : prev))), []);

  return { text, setText, reports, setReports, next, more: () => void load(next, text) };
}

function Search({ label, text, onText, extra }: { label: string; text: string; onText: (v: string) => void; extra?: ReactNode }) {
  return (
    <div className="board-toolbar">
      <TextField
        label={label}
        hideLabel
        size="sm"
        type="search"
        icon={<SearchIcon />}
        value={text}
        onChange={(e) => onText(e.target.value)}
        onClear={text ? () => onText('') : undefined}
        placeholder="Номер, текст, автор"
        className="board-search"
      />
      {extra}
    </div>
  );
}

/** Пт3: the backlog is a page of its own — reports of the first stage; «Взять в разработку» moves one to «Требования». */
export function BacklogPage({ onError, onMoved, renderDetail }: { onError: OnError; onMoved?: Moved; renderDetail: RenderDetail }) {
  const { text, setText, reports, setReports, next, more } = usePages('backlog', onError);
  const [openId, setOpenId] = useState<number | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [problem, setProblem] = useState('');

  const take = async (report: BugReportListItem) => {
    setBusy(report.id);
    setProblem('');
    try {
      await bugApi.update(report.id, { status: 'requirements', expectedStatus: report.status });
      onMoved?.(report.project, report.status, 'requirements');
      setReports((prev) => (prev ?? []).filter((r) => r.id !== report.id));
      if (openId === report.id) setOpenId(null);
    } catch (err) {
      setProblem(`${reportLabel(report)}: ${err instanceof Error && err.message ? err.message : 'не удалось взять в разработку'}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="board bug-backlog">
      <Search label="Поиск по бэклогу" text={text} onText={setText} />
      {problem && (
        <div role="alert" className="board-problem">
          <Banner tone="error" onClose={() => setProblem('')} closeLabel="Скрыть">
            {problem}
          </Banner>
        </div>
      )}
      {reports === null ? (
        <Spinner label="Загрузка бэклога" />
      ) : reports.length === 0 ? (
        <EmptyState compact>В бэклоге пусто</EmptyState>
      ) : (
        <div className="epic-backlog" aria-label="Бэклог">
          {reports.map((report) => (
            <Fragment key={report.id}>
              <div className={`bug-backlog-row${openId === report.id ? ' is-open' : ''}`}>
                <span className="mono" data-bug-open="">
                  {reportLabel(report)}
                </span>
                <button type="button" className="epic-backlog-title" data-pii="" onClick={() => setOpenId(openId === report.id ? null : report.id)}>
                  {firstLine(report)}
                </button>
                <span className="board-card-meta" data-pii="">
                  {formatDateTime(report.createdAt)}
                </span>
                <Button size="sm" variant="tonal" disabled={busy === report.id} onClick={() => void take(report)}>
                  Взять в разработку
                </Button>
              </div>
              {openId === report.id && (
                <RevealOnOpen>
                {renderDetail(
                  report.id,
                  (changed) => {
                    if (changed.status !== 'backlog') setReports((prev) => (prev ?? []).filter((r) => r.id !== report.id));
                    if (changed.status !== report.status) onMoved?.(report.project, report.status, changed.status);
                  },
                  () => {
                    onMoved?.(report.project, report.status, null);
                    setOpenId(null);
                    setReports((prev) => (prev ?? []).filter((r) => r.id !== report.id));
                  },
                )}
                </RevealOnOpen>
              )}
            </Fragment>
          ))}
        </div>
      )}
      {next && (
        <Button size="sm" variant="quiet" className="bug-more" onClick={more}>
          Ещё
        </Button>
      )}
    </div>
  );
}

const ALL_COLUMNS: Column<BugReportListItem>[] = [
  { key: 'number', header: '№', width: 84, value: (r) => reportValue(r, 'number'), render: (r) => <span className="mono board-nowrap" data-bug-open="">{reportLabel(r)}</span> },
  {
    key: 'text',
    header: 'Текст',
    minWidth: 200,
    value: (r) => reportValue(r, 'text'),
    render: (r) => (
      <span className="bug-all-cell">
        {r.repeat && <RepeatBadge repeat={r.repeat} />}
        <span className="bug-all-text" data-pii="">
          {firstLine(r)}
        </span>
      </span>
    ),
  },
  { key: 'stage', header: 'Этап', width: 150, value: (r) => reportValue(r, 'stage'), render: (r) => <span className="board-nowrap" data-bug-open="">{stageLabel(r.status)}</span> },
  { key: 'resolution', header: 'Резолюция', width: 150, value: (r) => reportValue(r, 'resolution'), render: (r) => <span className="board-nowrap" data-bug-open="">{resolutionOf(r.status) ?? '—'}</span> },
  { key: 'date', header: 'Дата', width: 150, value: (r) => reportValue(r, 'date'), render: (r) => <span className="board-nowrap" data-pii="">{formatDateTime(r.createdAt)}</span> },
];

/** Пт4, Рм1: the only full list — every report of every stage, page by page, in the library's `DataTable` (sort by every column). */
export function AllReports({ onError, onMoved, renderDetail }: { onError: OnError; onMoved?: Moved; renderDetail: RenderDetail }) {
  const [repeating, setRepeating] = useState<RepeatMode>('');
  const { text, setText, reports, setReports, next, more } = usePages(undefined, onError, repeating);
  const [openId, setOpenId] = useState<number | null>(null);
  const [epicIds, setEpicIds] = useState<EpicIds>(null);
  const rows = reports && reports.filter((r) => inEpic(r.id, epicIds));
  const open = openId === null ? undefined : rows?.find((r) => r.id === openId);

  return (
    <div className="board bug-all-reports">
      <DataTable
        className="bug-fixed-table bug-all-table"
        label="Все задачи"
        columns={ALL_COLUMNS}
        rows={rows ?? []}
        rowKey={(report) => report.id}
        pageSize={0}
        loading={rows === null}
        toolbar={<Search label="Поиск по репортам" text={text} onText={setText} extra={
              <>
                <EpicFilter onChange={setEpicIds} onError={onError} />
                <RepeatFilter value={repeating} onChange={setRepeating} />
              </>
            }
          />
        }
        rowTone={(report) => (report.id === openId ? 'selected' : undefined)}
        onRowClick={(report) => setOpenId(openId === report.id ? null : report.id)}
        empty={<EmptyState compact>Репортов нет</EmptyState>}
      />
      {open && (
        <RevealOnOpen key={open.id}>
        <div className="bug-all-detail" data-report={open.id}>
          {renderDetail(
            open.id,
            (changed) => {
              if (changed.status !== open.status) onMoved?.(open.project, open.status, changed.status);
              setReports((prev) => (prev ?? []).map((r) => (r.id === changed.id ? { ...r, status: changed.status, duplicateOf: changed.duplicateOf } : r)));
            },
            () => {
              onMoved?.(open.project, open.status, null);
              setOpenId(null);
              setReports((prev) => (prev ?? []).filter((r) => r.id !== open.id));
            },
          )}
        </div>
        </RevealOnOpen>
      )}
      {next && (
        <Button size="sm" variant="quiet" className="bug-more" onClick={more}>
          Ещё
        </Button>
      )}
    </div>
  );
}
